import { ERROR_CODES, errorResponseSchema } from '@kraaapf/contracts';

import { PortalClientError } from './errors';

import type { z } from 'zod';

/**
 * Der einzige Weg vom Browser zum Master.
 *
 * Dieselbe Naht wie `node-gateway` im Master: Es gibt genau eine Stelle, die
 * HTTP spricht. In den Anwendungen ist `fetch` deshalb per ESLint verboten —
 * wer Daten braucht, geht hier durch.
 *
 * ## Was der Rahmen leistet
 *
 * 1. **Cookies mitschicken.** `credentials: 'include'`; ohne diese Zeile
 *    ginge das Sitzungs-Cookie im Entwicklungsserver nicht mit.
 * 2. **Die Antwort gegen den Vertrag prüfen.** Ein Feld, das der Master
 *    umbenannt hat, fällt hier auf und nicht als `undefined` in einer Tabelle.
 * 3. **Fehler übersetzen.** Aus `ErrorResponse` wird ein `PortalClientError`
 *    mit stabilem Code, Meldung und Handlungsanweisung.
 * 4. **Bei 401 genau einmal auffrischen** — und zwar gebündelt.
 */

export interface PortalClientOptions {
  /**
   * Die Basis aller Pfade, in Produktion `/api`.
   *
   * Oberfläche und API liegen unter derselben Herkunft (Entscheidung M4); die
   * Edge trennt den Präfix ab, bevor sie an den Master weiterreicht. In der
   * Entwicklung tut das der Vite-Proxy — damit ist der Weg in beiden
   * Umgebungen derselbe.
   */
  readonly baseUrl: string;
  /**
   * Wird gerufen, wenn auch die Auffrischung scheitert.
   *
   * Die Oberfläche räumt daraufhin ihren Zustand ab und zeigt die Anmeldung.
   * Der Client selbst kennt keinen Router und leitet nichts um.
   */
  readonly onUnauthenticated?: () => void;
  /** Ausschließlich für Tests. */
  readonly fetchImpl?: typeof fetch;
}

export interface RequestOptions {
  readonly query?: Record<string, string | number | boolean | undefined>;
  /**
   * Bei 401 **nicht** auffrischen.
   *
   * Gesetzt für die Anmeldewege selbst: Eine Auffrischung nach einem
   * gescheiterten Anmeldeversuch wäre eine zweite Ablehnung und, schlimmer, ein
   * zweiter Eintrag im Prüfprotokoll.
   */
  readonly skipRefresh?: boolean;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export class PortalClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly onUnauthenticated: (() => void) | null;

  /**
   * Die laufende Auffrischung, falls es eine gibt.
   *
   * **Der Grund für dieses Feld**: Eine Ansicht stellt beim Öffnen fünf
   * Abfragen gleichzeitig. Läuft das Zugriffstoken ab, bekommen alle fünf eine
   * 401 — und ohne Bündelung liefen fünf Auffrischungen los. Vier davon legten
   * einen bereits rotierten Token vor, und die Wiederverwendungserkennung im
   * Master zöge die ganze Sitzungskette zurück. Der Betreiber wäre abgemeldet,
   * und im Log stünde ein Diebstahlverdacht.
   */
  private refreshInFlight: Promise<boolean> | null = null;

  public constructor(options: PortalClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.onUnauthenticated = options.onUnauthenticated ?? null;
  }

  public async get<T>(path: string, schema: z.ZodType<T>, options?: RequestOptions): Promise<T> {
    return this.request('GET', path, schema, undefined, options);
  }

  public async post<T>(
    path: string,
    schema: z.ZodType<T>,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request('POST', path, schema, body, options);
  }

  public async patch<T>(
    path: string,
    schema: z.ZodType<T>,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request('PATCH', path, schema, body, options);
  }

  /** Ein `DELETE`, das eine Antwort liefert — etwa die Site im Zustand `deleted`. */
  public async del<T>(path: string, schema: z.ZodType<T>, options?: RequestOptions): Promise<T> {
    return this.request('DELETE', path, schema, undefined, options);
  }

  /**
   * Ein Aufruf ohne Antwortkörper (204).
   *
   * Eigene Methode statt eines `z.void()`-Schemas: Ein leerer Körper ist kein
   * JSON, und `response.json()` darauf wirft. Der Unterschied gehört an die
   * Aufrufstelle und nicht in eine Sonderbehandlung tief im Rahmen.
   */
  public async send(
    method: Method,
    path: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<void> {
    await this.request(method, path, null, body, options);
  }

  /** Die vollständige URL eines Pfades — für den Ereignisstrom, der kein `fetch` ist. */
  public urlFor(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  // -- Innenleben -------------------------------------------------------------

  private async request<T>(
    method: Method,
    path: string,
    schema: z.ZodType<T> | null,
    body: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    const response = await this.perform(method, path, body, options);

    if (response.status === 204) {
      return undefined as T;
    }

    const payload: unknown = await response.json().catch(() => null);

    if (schema === null) {
      return undefined as T;
    }

    const parsed = schema.safeParse(payload);

    if (!parsed.success) {
      throw new PortalClientError({
        code: 'CONTRACT_MISMATCH',
        status: response.status,
        message: `Die Antwort auf ${method} ${path} entspricht nicht dem Vertrag: ${parsed.error.issues
          .map((issue) => `${issue.path.join('.')} ${issue.message}`)
          .join('; ')}`,
      });
    }

    return parsed.data;
  }

  /**
   * Führt die Anfrage aus — und wiederholt sie **einmal** nach einer
   * erfolgreichen Auffrischung.
   */
  private async perform(
    method: Method,
    path: string,
    body: unknown,
    options: RequestOptions,
  ): Promise<Response> {
    const response = await this.fetchOnce(method, path, body, options);

    if (response.status !== 401 || options.skipRefresh === true) {
      await assertOk(response, method, path);

      return response;
    }

    if (!(await this.refresh())) {
      this.onUnauthenticated?.();
      await assertOk(response, method, path);

      return response;
    }

    const retried = await this.fetchOnce(method, path, body, options);

    await assertOk(retried, method, path);

    return retried;
  }

  private async fetchOnce(
    method: Method,
    path: string,
    body: unknown,
    options: RequestOptions,
  ): Promise<Response> {
    // Relativ zur Herkunft der Seite: In Produktion ist das die Edge, in der
    // Entwicklung der Vite-Server, der `/api` weiterreicht.
    const url = new URL(this.urlFor(path), globalThis.location.origin);

    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }

    try {
      return await this.fetchImpl(url.toString(), {
        method,
        // Ohne diese Zeile schickt der Browser das Sitzungs-Cookie nicht mit,
        // sobald Oberfläche und API nicht dieselbe Herkunft haben — also
        // genau in der Entwicklung.
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch (error) {
      throw new PortalClientError({
        code: 'NETWORK_ERROR',
        status: 0,
        message:
          error instanceof Error
            ? `Der Master ist nicht erreichbar (${error.message}).`
            : 'Der Master ist nicht erreichbar.',
      });
    }
  }

  /**
   * Frischt die Sitzung auf — höchstens eine Auffrischung gleichzeitig.
   *
   * Alle Wartenden bekommen dieselbe Zusage; die Begründung steht am Feld
   * `refreshInFlight`.
   */
  private async refresh(): Promise<boolean> {
    this.refreshInFlight ??= this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<boolean> {
    try {
      const response = await this.fetchOnce('POST', '/auth/refresh', undefined, {});

      return response.ok;
    } catch {
      return false;
    }
  }
}

/** Übersetzt eine Fehlerantwort des Masters. */
async function assertOk(response: Response, method: string, path: string): Promise<void> {
  if (response.ok) {
    return;
  }

  const payload: unknown = await response.json().catch(() => null);
  const parsed = errorResponseSchema.safeParse(payload);

  if (parsed.success) {
    throw new PortalClientError({
      code: parsed.data.code,
      status: parsed.data.statusCode,
      message: parsed.data.message,
      correlationId: parsed.data.correlationId,
      ...(parsed.data.details === undefined ? {} : { fieldErrors: parsed.data.details }),
    });
  }

  // Kein Fehlerformat: Dann kam die Antwort nicht vom Master, sondern von
  // etwas dazwischen — einem Proxy, einer Edge, einer Fehlerseite.
  throw new PortalClientError({
    code: response.status >= 500 ? ERROR_CODES.upstreamUnavailable : ERROR_CODES.internal,
    status: response.status,
    message: `${method} ${path} beantwortet mit HTTP ${String(response.status)}, und die Antwort stammt nicht vom Master.`,
  });
}
